import { useState, useEffect, useRef, useCallback } from "react";
import { v4 as uuidv4 } from 'uuid';

export default function ConfigLevel({ updateBlock, block, setEditingBlock, setBlocks, blocks }) {
    // Initialize levels directly on the block like ConfigCards does
    if (block.levels === undefined) {
        block.levels = [
            {
                id: 'level-' + Date.now(),
                title: "Nível 1",
                description: "Descrição do nível 1",
                percent: 50,
                style: 'dashed',
                color: 'red-green',
                subIndicators: ["Baixo", "Médio", "Alto", "Ótimo", "Excelente"]
            }
        ];
    }

    // Initialize levels state from block.levels
    const [levels, setLevels] = useState(() => {
        return block.levels;
    });

    const [draggedIndex, setDraggedIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);

    // Update block when levels change
    
    function handleColumnsChange(e) {
        const newColumns = parseInt(e.target.value, 10);
        updateBlock(block.id, 'columns', newColumns);
    }

    function handleLevelChange(levelIndex, field, value) {
        // Follow ConfigCards pattern - mutate block directly then call updateBlock
        block.levels[levelIndex][field] = value;
        setLevels([...block.levels]);
        updateBlock(block.id, { ...block, levels: block.levels });
    }

    function handleAddLevel() {
        const newLevel = {
            id: 'level-' + Date.now(),
            title: "Nova Métrica",
            description: "Descrição da nova métrica",
            percent: 50,
            subIndicators: ["Baixo", "Médio", "Alto", "Ótimo", "Excelente"],
            style: 'dashed',
            color: 'red-green'
        };
        // Follow ConfigCards pattern - mutate block directly then call updateBlock
        block.levels.push(newLevel);
        setLevels([...block.levels]);
        updateBlock(block.id, { ...block, levels: block.levels });
    }

    function handleCopyLevel(levelIndex) {
        const levelToCopy = block.levels[levelIndex];
        if (!levelToCopy) return;
        const copiedLevel = {
            ...levelToCopy,
            id: 'level-' + Date.now(),
            title: `${levelToCopy.title}`
        };
        // Follow ConfigCards pattern - mutate block directly then call updateBlock
        block.levels.push(copiedLevel);
        setLevels([...block.levels]);
        updateBlock(block.id, { ...block, levels: block.levels });
    }

    function handleDeleteLevel(levelIndex) {
        if (block.levels.length <= 1) return; // Don't delete if it's the last level
        // Follow ConfigCards pattern - mutate block directly then call updateBlock
        block.levels.splice(levelIndex, 1);
        setLevels([...block.levels]);
        updateBlock(block.id, { ...block, levels: block.levels });
    }

    // Drag and Drop handlers
    function handleDragStart(e, index) {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/html', e.target.outerHTML);
        e.target.style.opacity = '0.5';
    }

    function handleDragEnd(e) {
        e.target.style.opacity = '';
        setDraggedIndex(null);
        setDragOverIndex(null);
    }

    function handleDragOver(e) {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    }

    function handleDragEnter(e, index) {
        e.preventDefault();
        setDragOverIndex(index);
    }

    function handleDragLeave(e) {
        // Only clear if we're leaving the entire drop zone
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setDragOverIndex(null);
        }
    }

    function handleDrop(e, dropIndex) {
        e.preventDefault();
        
        if (draggedIndex === null || draggedIndex === dropIndex) {
            return;
        }

        // Follow ConfigCards pattern - mutate block directly
        const [draggedLevel] = block.levels.splice(draggedIndex, 1);
        block.levels.splice(dropIndex, 0, draggedLevel);
        
        setLevels([...block.levels]);
        updateBlock(block.id, { ...block, levels: block.levels });

        setDraggedIndex(null);
        setDragOverIndex(null);
    }

   

    return (
        <div className="space-y-4">
            {/* Column Configuration */}
            <div>
                <label className="block font-semibold mb-2 text-gray-700">Layout:</label>
                <div className="p-3 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg border border-gray-200">
                    <select
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        value={block.columns || 1}
                        onChange={handleColumnsChange}
                    >
                        <option value={1}>1 Coluna</option>
                        <option value={2}>2 Colunas</option>
                    </select>
                </div>
            </div>

            {/* Levels Management */}
            <div>
                <div className="flex justify-between items-center mb-3">
                    <label className="font-semibold text-gray-700 flex items-center gap-2">
                        <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                        Níveis ({levels.length})
                    </label>
                    <div className="flex gap-2">
                        <button
                            onClick={handleAddLevel}
                            className="px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg text-sm hover:from-green-600 hover:to-green-700 transition-all shadow-sm hover:shadow-md flex items-center gap-1"
                            title="Adicionar Nível"
                        >
                            <span className="text-lg">+</span>
                            Add
                        </button>
                    </div>
                </div>

                {/* Level Cards with Inline Editing */}
                <div className="space-y-4">
                    {levels.map((level, index) => (
                        <div
                            key={level.id}
                            draggable
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragEnd={handleDragEnd}
                            onDragOver={handleDragOver}
                            onDragEnter={(e) => handleDragEnter(e, index)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, index)}
                            className={`
                                bg-white rounded-lg border-2 shadow-sm transition-all cursor-move group
                                ${dragOverIndex === index && draggedIndex !== index 
                                    ? 'border-blue-400 bg-blue-50 scale-105' 
                                    : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                                }
                                ${draggedIndex === index ? 'opacity-50 scale-95' : ''}
                            `}
                        >
                            {/* Header with drag handle and controls */}
                            <div className="flex items-center justify-between p-4 border-b border-gray-100">
                                <div className="flex items-center gap-3">
                                    {/* Drag Handle */}
                                    <div className="flex flex-col justify-center text-gray-400 group-hover:text-gray-600 cursor-move">
                                        <div className="w-1 h-1 bg-current rounded-full mb-1"></div>
                                        <div className="w-1 h-1 bg-current rounded-full mb-1"></div>
                                        <div className="w-1 h-1 bg-current rounded-full mb-1"></div>
                                        <div className="w-1 h-1 bg-current rounded-full mb-1"></div>
                                        <div className="w-1 h-1 bg-current rounded-full"></div>
                                    </div>
                                    
                                    {/* Level number badge */}
                                    <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-bold">
                                        {index + 1}
                                    </div>
                                    
                                    <div className="text-sm font-medium text-gray-600">
                                        Nível {index + 1}
                                    </div>
                                </div>
                                
                                {/* Action buttons */}
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => handleCopyLevel(index)}
                                        className="px-3 py-1 bg-blue-100 text-blue-600 rounded text-xs hover:bg-blue-200 transition-colors"
                                        title="Copiar Nível"
                                    >
                                        📋
                                    </button>
                                    <button
                                        onClick={() => handleDeleteLevel(index)}
                                        className="px-3 py-1 bg-red-100 text-red-600 rounded text-xs hover:bg-red-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        title="Deletar Nível"
                                        disabled={levels.length <= 1}
                                    >
                                        🗑️
                                    </button>
                                </div>
                            </div>

                            {/* Editing Fields */}
                            <div className="p-4 space-y-4">
                                {/* Title and Percentage Row */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Título:</label>
                                        <input
                                            type="text"
                                            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                                            value={level.title}
                                            onChange={(e) => handleLevelChange(index, 'title', e.target.value)}
                                            placeholder="Nome do nível"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Percentual:</label>
                                        <input
                                            type="number"
                                            min="0"
                                            max="100"
                                            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                                            value={level.percent}
                                            onChange={(e) => handleLevelChange(index, 'percent', parseInt(e.target.value, 10) || 0)}
                                            placeholder="0-100"
                                        />
                                    </div>
                                </div>

                                {/* Description */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Descrição:</label>
                                    <input
                                        type="text"
                                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                                        value={level.description}
                                        onChange={(e) => handleLevelChange(index, 'description', e.target.value)}
                                        placeholder="Descrição do nível"
                                    />
                                </div>

                                {/* Style and Color Row */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Tipo:</label>
                                        <select
                                            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                                            value={level.style}
                                            onChange={(e) => handleLevelChange(index, 'style', e.target.value)}
                                        >
                                            <option value="dashed">📏 Traço</option>
                                            <option value="solid">➖ Linha</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Cor:</label>
                                        <div className="flex gap-2">
                                            <select
                                                className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                                                value={level.color}
                                                onChange={(e) => handleLevelChange(index, 'color', e.target.value)}
                                            >
                                                <option value="red-green">🔴🟢 </option>
                                                <option value="green-red">🟢🔴 </option>
                                                <option value="red">🔴 </option>
                                                <option value="green">🟢 </option>
                                                <option value="blue">🔵 </option>
                                            </select>
                                          
                                        </div>
                                    </div>
                                </div>

                                {/* Indicators */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Indicadores:</label>
                                    <input
                                        type="text"
                                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                                        value={level.subIndicators.join(', ')}
                                        onChange={(e) => handleLevelChange(index, 'subIndicators', e.target.value.split(',').map(i => i.trim()))}
                                        placeholder="Baixo, Médio, Alto, Ótimo, Excelente"
                                    />
                                    <small className="text-gray-500 text-xs mt-1 block">💡 Separe os indicadores por vírgulas</small>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                
                <div className="mt-3 text-xs text-gray-500 text-center">
                    💡 Arraste e solte para reordenar os níveis
                </div>
            </div>
        </div>
    );
}